import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Button, Col } from "reactstrap";
import TextField from "../../../common/TextField";
import { toast } from "react-toastify";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { insertPropertyService } from "../../../../Services/CRMAdminServices/CRMPropertyService";
import { H3, H4 } from "../../../../AbstractElements";
import { propertyData } from "../FormData/CrmFormData";
import Dropdown from "../../../common/DropDown";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { getAllAreasSuburb, getAllStates } from "../../../../Services/MMPClientServices/PropertySearchService";
import DropdownWithValueName from "../../../common/DropdownWithValueName";
import DollarInput from "../../../common/DollarInput";
import DecimalInput from "../../../common/DecimalInput";
import { RootState } from "../../../../ReduxToolkit/Store";
import { useSelector } from "react-redux";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import { getAllBuildersService } from "../../../../Services/CRMAdminServices/CRMBuildersService";
import SelectDatePicker from "../../../common/SelectDatePicker";
import property from '../../MMPClient/PropertySearch/property.json';

export default function AddProperty() {
  const queryClient = useQueryClient();
  const navigate = useSafeNavigate();
  const [formData, setFormData] = useState<any>(propertyData);
  const [statesData, setStatesData] = useState([]);
  const [suburb, setSuburb] = useState<any[]>([]);
  const [areasData, setAreasData] = useState([]);
  const [buildersOptions, setBuildersOptions] = useState<any[]>([]);
  const [formErrors, setFormErrors] = useState<any>({});
  const userData = useSelector((state: RootState) => state.adminAuth);
  const [preview, setPreview] = useState<any>({
    NDISimageFile: null,
    BNDISimageFileuilderFaçadePageimageFile: null,
    FloorPlanimageFile: null,
    InclusionsimageFile: null,
    SubdivisionimageFile: null,
    EstateimageFile: null,
    brochurepdffile: null,
    // NDISimageFile:null,

  });

  const handleFileChange = (e: any, fieldName: any) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log("added file:", file);
      console.log("File selected for:", fieldName, file);
      setFormData({ ...formData, [fieldName]: file });

      // For PDF files, just set a flag; for images, create preview URL
      if (fieldName === "brochurepdffile") {
        setPreview({ ...preview, [fieldName]: true });
      } else {
        setPreview({ ...preview, [fieldName]: URL.createObjectURL(file) });
      }
    }
  };


  const handleValueChange = (name: string, value: string) => {
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
    setFormErrors((prevErrors: any) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const { mutate: addProperty, isPending: isAdding } = useMutationRequestQuery(
    "addProperty",
    insertPropertyService,
    {
      onSuccess: () => {
        toast.success("Property added successfully.");
        navigate("/CRMPropertySearch");
        queryClient.invalidateQueries({ queryKey: ["getAllProperties"] });
      },
      onError: () => {
        toast.error("Failed to add property.");
      },
    }
  );

  const handleSubmit = (e: any) => {
    e.preventDefault();

    /* Two Part contracts cannot be saved without both prices. Stamp duty on a Two Part
       contract is charged on the land alone, so a missing land or build figure makes the
       duty - and every downstream number that depends on it - wrong rather than absent. */
    const toNumber = (value: any) => parseFloat(`${value ?? ""}`.replace(/,/g, ""));
    if (formData.contractType === "Two Part") {
      const land = toNumber(formData.landPrice);
      const build = toNumber(formData.housePrice);
      const missing: any = {};
      if (!(land > 0)) {
        missing.landPrice = "Land price is required on a Two Part contract.";
      }
      if (!(build > 0)) {
        missing.housePrice = "Build price is required on a Two Part contract.";
      }
      if (Object.keys(missing).length > 0) {
        setFormErrors((prevErrors: any) => ({ ...prevErrors, ...missing }));
        toast.error(
          "A Two Part contract needs both a land price and a build price. Stamp duty is charged on the land only, so the property cannot be added without them."
        );
        return;
      }
    }

    const payload = {
      ...formData,
      isTitled: formData.isTitled === 'Yes' ? true : false,
      titled: formData.isTitled === 'Yes' ? 'Titled' : formData.titled || '',

      clientId: userData?.nameidentifier,
    };
    addProperty(payload);
  };

  const handleDatePickerChange = (name: string, value: Date | null | string) => {
    // Convert Date to string (in your desired format)
    let formatted = '';
    if (value instanceof Date) {
      const month = value.toLocaleString('default', { month: 'short' });
      const day = value.getDate();
      formatted = `${month} ${day}`;
    } else if (typeof value === 'string') {
      formatted = value;
    }
    handleValueChange(name, formatted);
  };
  const {
    data: allStatesData,
    isLoading: allStatesLoading,
    isError: allStatesIsError,
  } = useGetRequestQuery(
    ["getAllStates"],
    getAllStates,
    {
      enabled: true,
    }
  );

  useEffect(() => {
    if (allStatesData?.data && !allStatesIsError && !allStatesLoading) {
      const transformedData = allStatesData.data.map((state: any) => ({
        value: state.id,
        label: state.name
      }));
      setStatesData(transformedData);
      console.log(transformedData);
    }
  }, [allStatesData, allStatesIsError, allStatesLoading]);



  const {
    data: allAreasData,
    isLoading: allAreasLoading,
    isError: allAreasIsError,
  } = useGetRequestQuery(
    ["getAllAreasSuburb", formData.stateId],
    () => getAllAreasSuburb({
      searchId: formData.stateId,
      searchType: "area"
    }),
    {
      enabled: !!formData.stateId,
    }
  );

  useEffect(() => {
    if (allAreasData?.data && !allAreasIsError && !allAreasLoading) {
      const transformedData = allAreasData?.data?.map((area: any) => ({
        value: area.id,
        label: area.name
      }));
      setAreasData(transformedData);
    }
  }, [allAreasData, allAreasIsError, allAreasLoading]);


  const {
    data: allSuburbsData,
    isLoading: allSuburbsLoading,
    isError: allSuburbsIsError,
  } = useGetRequestQuery(
    ["getAllSuburbs", formData.stateId],
    () => getAllAreasSuburb({
      searchId: formData.stateId,
      searchType: "suburb"
    }),
    {
      enabled: !!formData.stateId,
    }
  );

  useEffect(() => {
    if (allSuburbsData?.data && !allSuburbsIsError && !allSuburbsLoading) {
      const transformedSuburbsData = allSuburbsData?.data
        ?.map((suburb: any) => ({
          value: suburb.id,
          label: suburb.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      setSuburb(transformedSuburbsData);
    }
  }, [allSuburbsData, allSuburbsIsError, allSuburbsLoading]);

  const {
    data: areaFromSuburbData,
    isLoading: areaFromSuburbLoading,
    isError: areaFromSuburbIsError,
  } = useGetRequestQuery(
    ["getAreaFromSuburb", formData.suburb],
    () => getAllAreasSuburb({
      searchId: formData.suburb,
      searchType: "area"
    }),
    {
      enabled: !!formData.suburb,
    }
  );

  useEffect(() => {
    if (areaFromSuburbData?.data && !areaFromSuburbIsError && !areaFromSuburbLoading && formData.suburb) {
      const transformedData = areaFromSuburbData.data
        .map((area: any) => ({
          value: area.id,
          label: area.name
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      setAreasData(transformedData);
      if (transformedData.length > 0) {
        handleValueChange('area', transformedData[0].value);
      }
    }
  }, [areaFromSuburbData, areaFromSuburbIsError, areaFromSuburbLoading, formData.suburb]);

  const calculateRentYield = useCallback(() => {
    const rentDollar = parseFloat(formData.rentDollar.replace(/,/g, "")) || 0;
    const housePrice = parseFloat(formData.housePrice.replace(/,/g, "")) || 0;
    const landPrice = parseFloat(formData.landPrice.replace(/,/g, "")) || 0;

    const price1Bedroom = housePrice + landPrice;

    const rentYield = price1Bedroom !== 0 ? ((rentDollar * 52) / price1Bedroom) * 100 : 0;
    return { rentYield: rentYield.toFixed(2), price1Bedroom: price1Bedroom.toLocaleString() };
  }, [formData.rentDollar, formData.housePrice, formData.landPrice]);

  const calculateHousePriceSqm = useCallback(() => {
    const housePrice = parseFloat(formData.housePrice.replace(/,/g, "")) || 0;
    const houseSize = parseFloat(formData.houseSize) || 0;

    const housePriceSqm = houseSize !== 0 ? housePrice / houseSize : 0;
    return housePriceSqm.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }, [formData.housePrice, formData.houseSize]);

  const calculateLandPriceSqm = useCallback(() => {
    const landPrice = parseFloat(formData.landPrice.replace(/,/g, "")) || 0;
    const landSize = parseFloat(formData.landSize) || 0;

    const landPriceSqm = landSize !== 0 ? landPrice / landSize : 0;
    return landPriceSqm.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }, [formData.landPrice, formData.landSize]);

  useEffect(() => {
    const { rentYield, price1Bedroom } = calculateRentYield();

    setFormData((prevData: any) => ({
      ...prevData,
      rentYield,
      price1Bedroom,
    }));
  }, [calculateRentYield]);

  useEffect(() => {
    const housePriceSqm = calculateHousePriceSqm();

    setFormData((prevData: any) => ({
      ...prevData,
      housePriceSqm,
    }));
  }, [calculateHousePriceSqm]);

  useEffect(() => {
    const landPriceSqm = calculateLandPriceSqm();

    setFormData((prevData: any) => ({
      ...prevData,
      landPriceSqm,
    }));
  }, [calculateLandPriceSqm]);


  // Fetch builders data
  const {
    data: buildersData,
    isLoading: buildersLoading,
    isError: buildersIsError,
  } = useGetRequestQuery(
    ["getAllBuilders"],
    getAllBuildersService,
    {
      enabled: true,
    }
  );

  useEffect(() => {
    if (buildersData?.data && !buildersIsError && !buildersLoading) {
      const transformedData = buildersData.data
      
        .filter((builder: any) => builder.isActive === true || builder.isActive === 1)
                  .map((builder: any) => ({
                      value: builder.supplierId,
                      label: builder.supplier
                  }));
      setBuildersOptions(transformedData);
    }
  }, [buildersData, buildersIsError, buildersLoading]);

  useEffect(() => {
    if (formData.contractType === "One Part") {
      setFormData((prevData: any) => {
        const cleanedLandPrice = `${prevData.landPrice ?? ""}`.replace(/,/g, "");
        if (cleanedLandPrice === "0") {
          return prevData;
        }
        return {
          ...prevData,
          landPrice: "0",
        };
      });
    }
  }, [formData.contractType]);

  const isOnePartSmsfContract = formData.contractType === "One Part";
  const isTwoPartContract = formData.contractType === "Two Part";

  /* A Two Part contract is a land contract plus a build contract, and stamp duty is
     charged on the land only. Both figures are therefore required - without them the
     duty, and so the client's maximum purchase price, cannot be calculated. A price of
     zero is treated as missing, not as a valid free block. */
  const landPriceValueRaw = `${formData.landPrice ?? ""}`;
  const landPriceNumericValue = parseFloat(landPriceValueRaw.replace(/,/g, ""));
  const isTwoPartContractLandPriceMissing =
    isTwoPartContract &&
    (landPriceValueRaw.trim() === "" || isNaN(landPriceNumericValue) || landPriceNumericValue <= 0);
  const landPriceError = Boolean(formErrors.landPrice) || isTwoPartContractLandPriceMissing;
  const landPriceErrorMessage =
    typeof formErrors.landPrice === "string" && formErrors.landPrice.length > 0
      ? formErrors.landPrice
      : isTwoPartContractLandPriceMissing
        ? "Land price is required on a Two Part contract - stamp duty is charged on the land."
        : "Land price is required!";

  const housePriceValueRaw = `${formData.housePrice ?? ""}`;
  const housePriceNumericValue = parseFloat(housePriceValueRaw.replace(/,/g, ""));
  const isTwoPartContractBuildPriceMissing =
    isTwoPartContract &&
    (housePriceValueRaw.trim() === "" || isNaN(housePriceNumericValue) || housePriceNumericValue <= 0);
  const housePriceError = Boolean(formErrors.housePrice) || isTwoPartContractBuildPriceMissing;
  const housePriceErrorMessage =
    typeof formErrors.housePrice === "string" && formErrors.housePrice.length > 0
      ? formErrors.housePrice
      : isTwoPartContractBuildPriceMissing
        ? "Build price is required on a Two Part contract."
        : "Build price is required!";

  return (
    <div className="px-4 py-4" style={{ minHeight: "300px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
      <Form style={{ boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "white", borderRadius: "8px", padding: "15px", paddingLeft: "30px" }}>
        <div className="row">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <H3 className="heading-color f-w-900">Add Property</H3>

            <Button
              style={{ marginRight: "32rem" }}
              onClick={handleSubmit}
              color="primary"
              disabled={isAdding}
              type="submit"
            >
              {isAdding ? "Loading..." : "Save Property"}
            </Button>
          </div>




          <div className="row">
            {/* LEFT FORM */}
            <div className="col-lg-6 col-md-8">
              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <DropdownWithValueName
                  label="Supplier:"
                  name="supplier"
                  options={buildersOptions}
                  value={formData.supplier}
                  setValue={handleValueChange}
                  passCol="col-10"
                  design="flex"
                  error={formErrors.supplier}
                  errorMessage="Supplier is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <TextField
                  label="Estate:"
                  type="text"
                  name="estate"
                  value={formData.estate}
                  setValue={handleValueChange}
                  placeholder="Enter estate"
                  passCol="col-10"
                  design="flex"
                  maxChars={50}
                  error={formErrors.estate}
                  errorMessage="Estate is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <TextField
                  label="Lot #"
                  type="text"
                  name="lotNumber"
                  value={formData.lotNumber}
                  setValue={handleValueChange}
                  placeholder="Enter lot number"
                  passCol="col-10"
                  design="flex"
                  maxChars={50}
                  error={formErrors.lotNumber}
                  errorMessage="Lot number is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <TextField
                  label="Property Address"
                  type="text"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  setValue={handleValueChange}
                  placeholder="Enter property address"
                  passCol="col-10"
                  design="flex"
                  maxChars={100}
                  error={formErrors.propertyAddress}
                  errorMessage="Property address is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <DropdownWithValueName
                  label="State:"
                  name="stateId"
                  value={formData.stateId}
                  setValue={handleValueChange}
                  passCol="col-10"
                  design="flex"
                  error={formErrors.stateId}
                  options={statesData}
                  errorMessage="State is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <DropdownWithValueName
                  label="Suburb:"
                  name="suburb"
                  value={formData.suburb}
                  setValue={handleValueChange}
                  passCol="col-10"
                  options={[...(suburb || [])].sort((a, b) => a.label.localeCompare(b.label))}
                  design="flex"
                  error={formErrors.suburb}
                  errorMessage="Suburb is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <DropdownWithValueName
                  label="Area:"
                  name="area"
                  value={formData.area}
                  setValue={handleValueChange}
                  passCol="col-10"
                  design="flex"
                  error={formErrors.area}
                  options={[...(areasData || []) as { label: string; value: string }[]].sort((a, b) => a.label.localeCompare(b.label))}
                  errorMessage="Area is required!"
                />
              </Col>

              <div style={{ borderBottom: '3px solid #0000ff', width: '100%' }} className="my-3"></div>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <Dropdown
                  label="Property Type:"
                  name="propertyType"
                  value={formData.propertyType}
                  setValue={handleValueChange}
                  options={property.propType}
                  passCol="col-10"
                  design="flex"
                  error={formErrors.propertyType}
                  errorMessage="Property type is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <Dropdown
                  label="Contract Type:"
                  name="contractType"
                  options={['Two Part', 'One Part',]}
                  value={formData.contractType}
                  setValue={handleValueChange}
                  passCol="col-10"
                  design="flex"
                  error={formErrors.contractType}
                  errorMessage="Contract type is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <Dropdown
                  label="Construction:"
                  name="offThePlan"
                  options={["To be constructed", "Near Completion", "Completed"]}
                  value={formData.offThePlan}
                  setValue={handleValueChange}
                  passCol="col-10"
                  design="flex"
                  error={formErrors.offThePlan}
                  errorMessage="offThePlan type is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <Dropdown
                  label="Storey:"
                  name="storey"
                  value={formData.storey}
                  setValue={handleValueChange}
                  passCol="col-10"
                  design="flex"
                  error={formErrors.storey}
                  options={['Single', 'Double']}
                  errorMessage="Storey is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <Dropdown
                  label="Bedrooms:"
                  name="bedrooms"
                  value={formData.bedrooms}
                  setValue={handleValueChange}
                  passCol="col-10"
                  design="flex"
                  options={['1', '2', '3', '4', '5', '6', '7', '8', '9']}
                  error={formErrors.bedrooms}
                  errorMessage="Bedrooms count is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <Dropdown
                  label="Bath:"
                  name="bath"
                  value={formData.bath}
                  setValue={handleValueChange}
                  passCol="col-10"
                  options={['1', '2', '3', '4', '5', '6', '7', '8', '9']}
                  design="flex"
                  error={formErrors.bath}
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <Dropdown
                  label="Carspots:"
                  name="carspots"
                  options={['1', '2', '3', '4', '5', '6', '7', '8', '9']}
                  value={formData.carspots}
                  setValue={handleValueChange}
                  passCol="col-10"
                  design="flex"
                  error={formErrors.carspots}
                  errorMessage="Number of carspots is required!"
                />
              </Col>

              <div style={{ borderBottom: '3px solid #0000ff', width: '100%' }} className="my-3"></div>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <DollarInput
                  label="Package Price:"
                  name="price1Bedroom"
                  value={formData.price1Bedroom}
                  setValue={handleValueChange}
                  placeholder="Enter package price"
                  passCol="col-10"
                  design="flex"
                  limit={8}
                  disabled
                  error={formErrors.price1Bedroom}
                  errorMessage="Package price is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <DollarInput
                  label="House Price:"
                  name="housePrice"
                  value={formData.housePrice}
                  setValue={handleValueChange}
                  placeholder={isTwoPartContract ? "Enter build price (required)" : "Enter house price"}
                  passCol="col-10"
                  design="flex"
                  limit={8}
                  error={housePriceError}
                  errorMessage={housePriceErrorMessage}
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <TextField
                  label="House Size:"
                  type="number"
                  name="houseSize"
                  value={formData.houseSize}
                  setValue={handleValueChange}
                  placeholder="Enter house size"
                  passCol="col-10"
                  design="flex"
                  maxChars={4}
                  error={formErrors.houseSize}
                  errorMessage="House size is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <DollarInput
                  label="House $ SQM:"
                  name="housePriceSqm"
                  value={formData.housePriceSqm}
                  setValue={handleValueChange}
                  placeholder="Enter house price per SQM"
                  passCol="col-10"
                  design="flex"
                  limit={8}
                  disabled
                  error={formErrors.housePriceSqm}
                  errorMessage="House price per SQM is required!"
                />
              </Col>

              <div style={{ borderBottom: '3px solid #0000ff', width: '100%' }} className="my-3"></div>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <DollarInput
                  label="Land Price:"
                  name="landPrice"
                  value={formData.landPrice}
                  setValue={handleValueChange}
                  placeholder="Enter land price"
                  passCol="col-10"
                  design="flex"
                  error={landPriceError}
                  limit={8}
                  errorMessage={landPriceErrorMessage}
                  disabled={isOnePartSmsfContract}
                  style={isOnePartSmsfContract ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : undefined}
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <TextField
                  label="Land Size:"
                  type="number"
                  name="landSize"
                  value={formData.landSize}
                  setValue={handleValueChange}
                  placeholder="Enter land size"
                  passCol="col-10"
                  design="flex"
                  maxChars={4}
                  error={formErrors.landSize}
                  errorMessage="Land size is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <DollarInput
                  label="Land $ SQM:"
                  name="landPriceSqm"
                  value={formData.landPriceSqm}
                  setValue={handleValueChange}
                  placeholder="Enter land price per SQM"
                  passCol="col-10"
                  design="flex"
                  error={formErrors.landPriceSqm}
                  limit={8}
                  disabled
                  errorMessage="Land price per SQM is required!"
                />
              </Col>

              <div style={{ borderBottom: '3px solid #0000ff', width: '100%' }} className="my-3"></div>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <DollarInput
                  label="Rent per Week:"
                  name="rentDollar"
                  value={formData.rentDollar}
                  setValue={handleValueChange}
                  placeholder="Enter rent per week"
                  passCol="col-10"
                  design="flex"
                  limit={8}
                  error={formErrors.rentDollar}
                  errorMessage="Rent per week is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <DecimalInput
                  label="Rent Yield:"
                  name="rentYield"
                  value={formData.rentYield}
                  setValue={handleValueChange}
                  placeholder="Enter rent yield"
                  passCol="col-10"
                  design="flex"
                  limit={4}
                  disabled
                  error={formErrors.rentYield}
                  errorMessage="Rent yield is required!"
                />
              </Col>

              <Col className="responsive-col-lead" xs={12} style={{ width: '80%' }}>
                <Dropdown
                  label="Titled:"
                  name="isTitled"
                  options={['Yes', 'No']}
                  value={formData.isTitled}
                  setValue={handleValueChange}
                  passCol="col-10"
                  design="flex"
                  error={formErrors.isTitled}
                  errorMessage="Titled status is required!"
                />
                {formData.isTitled === 'No' && (
                  <div className='d-flex'>
                    <Col className="responsive-col-lead" xs={12} style={{ width: '100%' }}>
                      <div className="d-flex align-items-center" style={{ gap: 60 }}>
                        <label style={{ fontWeight: 500, marginBottom: 0, whiteSpace: 'nowrap' }}>Titled Date:</label>
                        <SelectDatePicker
                          label=""
                          name="titled"
                          formate="MMM d"
                          value={formData.titled}
                          setValue={handleDatePickerChange}
                          passCol="col-9"
                          design="flex"
                        />
                        {formErrors.titled && (
                          <div className="invalid-feedback" style={{ display: 'block' }}>{formErrors.titled}</div>
                        )}
                      </div>
                      {formErrors.titled && (
                        <div className="invalid-feedback" style={{ display: 'block' }}>{formErrors.titled}</div>
                      )}
                    </Col>
                  </div>
                )}
              </Col>

            </div>

            {/* RIGHT UPLOADS */}
            <div className="col-lg-4 col-md-12">

              {/* Upload section goes here */}
              <div
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "16px",
                  background: "#fafafa"
                }}
              >
                {/* <H4 className="mb-3">Property Uploads</H4> */}

                {[
                  { label: "Façade", name: "BuilderFaçadePageimageFile" },
                  { label: "Floor Plan", name: "FloorPlanimageFile" },
                  { label: "Estate", name: "EstateimageFile" },
                  { label: "Subdivision", name: "SubdivisionimageFile" },
                  { label: "Inclusions", name: "InclusionsimageFile" },
                  { label: "Rent Appraisal", name: "NDISimageFile" },
                  { label: "Brochure PDF", name: "brochurepdffile" },
                ].map(({ label, name }) => (
                  <div key={name} className="mb-3">
                    <label className="form-label fw-semibold">{label}</label>

                    <input
                      type="file"
                      accept={name === "brochurepdffile" ? ".pdf" : "image/*"}
                      className="form-control"
                      onChange={(e) => handleFileChange(e, name)}
                    />

                    {preview[name] && (
                      name === "brochurepdffile" ? (
                        <div
                          style={{
                            marginTop: 8,
                            padding: 10,
                            backgroundColor: "#f0f0f0",
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <i className="fa fa-file-pdf-o" style={{ fontSize: 24, color: "#d32f2f" }}></i>
                          <span style={{ fontSize: 14 }}>PDF uploaded</span>
                        </div>
                      ) : (
                        <img
                          src={preview[name]}
                          alt={label}
                          style={{
                            marginTop: 8,
                            width: "100%",
                            height: 120,
                            objectFit: "cover",
                            borderRadius: 6,
                          }}
                        />
                      )
                    )}
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center my-3">
          <H4 className="heading-color f-w-900">Please make sure you have uploaded PROPERTY PACKAGE</H4>
        </div>


        <div className="d-flex gap-3 mt-3">
          <Button onClick={() => navigate("/CRMPropertySearch")} color="secondary">
            Back to Properties
          </Button>
          <Button onClick={handleSubmit} color="primary" disabled={isAdding} type="submit">
            {isAdding ? "Loading . . ." : "Save Property"}
          </Button>
        </div>
      </Form>
    </div>
  );

}